import { Component, computed, DestroyRef, effect, inject, input, linkedSignal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { AbstractControl, ReactiveFormsModule } from '@angular/forms';
import { provideIcons } from '@ng-icons/core';
import { lucideEye } from '@ng-icons/lucide';
import { HlmButtonImports } from '@spartan-ng/helm/button';
import { HlmCheckboxImports } from '@spartan-ng/helm/checkbox';
import { HlmDropdownMenuImports } from '@spartan-ng/helm/dropdown-menu';
import { HlmIconImports } from '@spartan-ng/helm/icon';
import { HlmTooltipImports } from '@spartan-ng/helm/tooltip';

import { HlmRadioGroupImports } from '@spartan-ng/helm/radio-group';
import { Cartographie } from '../../../../cartographie/models/cartographie.model';
import { Certification } from '../../../../croisement-référentiel/models/certification';
import { CroisementÉtat } from '../../../../croisement-référentiel/models/croisement-état';
import { GetIdFichePipe } from '../../../../croisement-référentiel/pipes/get-id-fiche.pipe';
import { CertificationStore } from '../../../../croisement-référentiel/store/certification.store';
import { Interrelation } from '../../../../référentiel-métier/référentiel-metier-interrelations/models/interrelation.model';
import { Aptitude } from '../../../../référentiel-métier/référentiel-métier-processus/processus-aptitude/models/aptitude.model';
import { Production } from '../../../../référentiel-métier/référentiel-métier-processus/processus-aptitude/models/production.model';
import { AptitudeStore } from '../../../../référentiel-métier/référentiel-métier-processus/processus-aptitude/store/aptitude.store';
import {
  CardCertificationComponent,
  ListActions,
} from '../../../../shared/components/card-certification/card-certification.component';
import { SidepanelService } from '../../../../shared/components/sidepanel/sidepanel.service';
import { URL_FRANCE_COMPETENCES_RECHERCHE } from '../../../../shared/models/urls-france-compétences';
import { TrierParPipe } from '../../../../shared/pipes/trier-par.pipe';
import { FormControlNames } from '../../../models/form-control-names.model';
import { DetailCertificationFormGroup, DetailObjectifsFormGroup } from '../detail-form-groups.types';
import { EtapeDetailFormService } from '../étape-detail-form.service';
import { DetailAptitudeItemComponent } from './aptitude-item/detail-aptitude-item.component';
import { FormDetailObjectifsAptitudesComponent } from './form-detail-objectifs-aptitudes/form-detail-objectifs-aptitudes.component';
import { InterrelationStore } from '../../../../référentiel-métier/référentiel-metier-interrelations/store/interrelation.store';
import { Référentiel } from '../../../../administration/gestion-référentiels/models/référentiel.model';
import { ReferentielStore } from '../../../../administration/gestion-référentiels/store/referentiel.store';

@Component({
  selector: 'app-detail-objectifs',
  imports: [
    ReactiveFormsModule,
    HlmButtonImports,
    HlmCheckboxImports,
    TrierParPipe,
    HlmDropdownMenuImports,
    HlmIconImports,
    CardCertificationComponent,
    HlmTooltipImports,
    HlmRadioGroupImports,
    DetailAptitudeItemComponent,
  ],
  templateUrl: './detail-objectifs.component.html',
  providers: [GetIdFichePipe, provideIcons({ lucideEye })],
})
export class DetailObjectifsComponent {
  readonly #aptitudeStore = inject(AptitudeStore);
  readonly #certificationStore = inject(CertificationStore);
  readonly #interrelationStore = inject(InterrelationStore);
  readonly #etapeFormService = inject(EtapeDetailFormService);
  readonly #referentielStore = inject(ReferentielStore);
  readonly #destroy = inject(DestroyRef);
  readonly #sidepanelService = inject(SidepanelService);
  readonly #getIdFiche = inject(GetIdFichePipe);

  readonly form = input.required<DetailObjectifsFormGroup>();
  readonly formCertifications = input.required<DetailCertificationFormGroup>();

  readonly certificationAttachee = linkedSignal(() => this.#certificationStore.selectedEntity());
  readonly aptitudesCertif = computed(
    () =>
      this.certificationAttachee()
        ?.blocsCompetences.flatMap((x) => x.competences)
        .flatMap((x) => x.aptitudes ?? [])
        .filter((apt1, i, list) => list.findIndex((apt2) => apt2.id === apt1.id) === i)
        .map((aptitude) => ({
          id: aptitude.id,
          idPhase: aptitude.idPhase,
          nom: aptitude.nom,
          productions: aptitude.productions,
          indice: aptitude.indice,
          indicesResultats: aptitude.indicesResultats,
          interrelations: this.#interrelationStore.parPhase(aptitude.idPhase),
        })) ?? [],
  );

  readonly cartographie = input.required<Cartographie>();
  readonly referentiel = input.required<Référentiel>();

  readonly etatsCroisement = CroisementÉtat;

  readonly certificationActions: ListActions[] = [
    {
      icon: 'lucideColumns2',
      label: 'Accéder au Croisement des référentiels',
      execute: (certification: Certification) => {
        const url = location.href;
        const fragments = url.split('/');
        fragments.splice(-2);
        fragments.push('croisement', certification.codeFiche);
        window.open(fragments.join('/'), '_blank');
      },
    },
    {
      icon: 'lucideSquareArrowOutUpRight',
      label: 'Accéder à France Compétences',
      execute: (certification: Certification) =>
        window.open(URL_FRANCE_COMPETENCES_RECHERCHE + this.#getIdFiche.transform(certification.codeFiche), '_blank'),
    },
  ];

  constructor() {
    effect(() => {
      const formCertifications = this.formCertifications();
      const certificationControl = formCertifications.get('certification');

      if (certificationControl) {
        certificationControl.valueChanges
          .pipe(takeUntilDestroyed(this.#destroy))
          .subscribe((r) => this.certificationAttachee.set(r));
      }
    });

    effect(() => {
      const cartographie = this.cartographie();
      this.#aptitudeStore.chargerToutes(cartographie.id);
      this.#referentielStore.chargerParCartographie(cartographie.id);
      this.#etapeFormService.referentiel.set(this.referentiel());
    });
  }

  manualAddAptitudes() {
    this.#sidepanelService.setAdditionalComponentToDisplay(
      "Ajouter d'autres aptitudes",
      FormDetailObjectifsAptitudesComponent,
      "Vous pouvez ajouter d'autres aptitudes. Il vous suffit de rechercher une aptitude existante dans n'importe quel référentiel. Ses interrelations et ses productions seront modifiables une fois que vous aurez validé.",
      {
        referentiel: this.referentiel(),
        idsAptitudes: [...this.aptitudesCertif().map((x) => x.id), ...this.aptitudes.value.map((x) => x.id)],
      },
    );
    this.#sidepanelService.open();
  }

  redirectToRessourcesPro(idProduction: string) {
    const url = location.href;
    const fragments = url.split('/').slice(0, 7);
    fragments.push(`ressources-professionnelles?idProduction=${idProduction}`);
    window.open(fragments.join('/'), '_blank');
  }

  get aptitudes() {
    return this.form().get(FormControlNames.APTITUDES) as AbstractControl<Aptitude[]>;
  }

  get interrelations() {
    return this.form().get(FormControlNames.INTERRELATIONS) as AbstractControl<Interrelation[]>;
  }

  get productions() {
    return this.form().get(FormControlNames.PRODUCTIONS) as AbstractControl<Production[]>;
  }
}
